import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, TextField, Button, Select, MenuItem, FormControl, InputLabel,
  InputAdornment, IconButton, Paper, Typography, CircularProgress,
  Chip, Alert, Collapse, LinearProgress, Tooltip, SelectChangeEvent
} from '@mui/material';
import {
  Visibility, VisibilityOff,
  PersonAddAlt1 as CreateIcon,
  CheckCircleOutline as SuccessIcon,
  ErrorOutline as ErrorIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import axios from 'axios';

// ─── Types ───────────────────────────────────────────────────────────────────
interface RoleOption {
  id: number;
  slug: string;
  name: string;
  level: number;
  targetInterface: 'admin' | 'dashboard';
  badge: { bg: string; text: string; border: string };
}

interface Region {
  id: number;
  name: string;
}

interface InitData {
  operator: { id: number; username: string; role: string; roleLevel: number; isSuperAdmin: boolean };
  roles: RoleOption[];
  regions: Region[];
}

interface CreateAccountProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function generatePassword(length = 10): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '0.9rem',
    backgroundColor: '#ffffff',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#94a3b8' },
    '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '1.5px' }
  },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
  '& .MuiFormHelperText-root': { fontSize: '0.72rem', mt: 0.5 }
};

const PHARMACY_ROLE_SLUGS = ['user']; // роли с полем "номер аптеки"
const STORAGE_ROLE_SLUGS = ['storage']; // роли с полем "наименование склада"
const DEFAULT_PASSWORD_ROLES: Record<string, string> = {
  user: 'ZerdePh' // дефолтный пароль для роли Аптека
};

// ─────────────────────────────────────────────────────────────────────────────
const CreateAccount: React.FC<CreateAccountProps> = ({ onSuccess, onCancel }) => {
  // Form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [roleSlug, setRoleSlug] = useState('');
  const [pharmacyNum, setPharmacyNum] = useState('');
  const [myPharmacy, setMyPharmacy] = useState('');
  const [cityId, setCityId] = useState<string>('');

  // UI state
  const [initData, setInitData] = useState<InitData | null>(null);
  const [initLoading, setInitLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginChecking, setLoginChecking] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string; timesheetInfo?: any } | null>(null);
  const [copied, setCopied] = useState(false);

  const loginCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Load init data ──────────────────────────────────────────────────────────
  const loadInit = useCallback(async () => {
    setInitLoading(true);
    setInitError(null);
    try {
      const res = await axios.get('/api/v2/create-account/init');
      if (res.data.success) {
        setInitData(res.data);
        // Автовыбор первой роли
        if (res.data.roles.length > 0) {
          const firstRole = res.data.roles[res.data.roles.length - 1]; // самая низкая роль
          setRoleSlug(firstRole.slug);
          setPassword(DEFAULT_PASSWORD_ROLES[firstRole.slug] || '');
        }
      } else {
        setInitError(res.data.message || 'Ошибка загрузки данных');
      }
    } catch (err: any) {
      setInitError(err.response?.data?.message || 'Нет доступа к форме создания пользователя');
    } finally {
      setInitLoading(false);
    }
  }, []);

  useEffect(() => { loadInit(); }, [loadInit]);

  // ── Check login uniqueness ─────────────────────────────────────────────────
  const checkLogin = useCallback(async (val: string) => {
    if (!val.trim()) { setLoginError(''); return; }
    setLoginChecking(true);
    try {
      const res = await axios.post('/api/v2/create-account/check-login', { username: val.trim() });
      setLoginError(res.data.exists ? 'Логин уже занят' : '');
    } catch {
      setLoginError('');
    } finally {
      setLoginChecking(false);
    }
  }, []);

  const handleUsernameChange = (val: string) => {
    setUsername(val);
    setLoginError('');
    if (loginCheckTimer.current) clearTimeout(loginCheckTimer.current);
    if (val.trim().length >= 2) {
      loginCheckTimer.current = setTimeout(() => checkLogin(val), 600);
    }
  };

  // ── Role change ────────────────────────────────────────────────────────────
  const handleRoleChange = (slug: string) => {
    setRoleSlug(slug);
    // Дефолтный пароль
    if (DEFAULT_PASSWORD_ROLES[slug]) {
      setPassword(DEFAULT_PASSWORD_ROLES[slug]);
    } else if (Object.values(DEFAULT_PASSWORD_ROLES).includes(password)) {
      setPassword('');
    }
    // Для admin-ролей очищаем pharmacyNum
    const role = initData?.roles.find(r => r.slug === slug);
    if (role?.targetInterface === 'admin') {
      setPharmacyNum('-');
    } else {
      setPharmacyNum('');
    }
  };

  // ── Generate password ──────────────────────────────────────────────────────
  const handleGeneratePassword = () => {
    const p = generatePassword(12);
    setPassword(p);
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || isCreating) return;

    setIsCreating(true);
    setResult(null);
    try {
      const res = await axios.post('/api/v2/create-account', {
        username: username.trim(),
        password,
        roleSlug,
        pharmacyNum: pharmacyNum.trim(),
        myPharmacy: myPharmacy.trim(),
        cityId
      });
      if (res.data.success) {
        setResult({ success: true, message: res.data.message, timesheetInfo: res.data.timesheetInfo });
        // Очистка формы
        setUsername('');
        setPassword(DEFAULT_PASSWORD_ROLES[roleSlug] || '');
        setPharmacyNum('');
        setMyPharmacy('');
        setCityId('');
        setLoginError('');
        if (onSuccess) setTimeout(onSuccess, 1200);
      } else {
        setResult({ success: false, message: res.data.message || 'Ошибка при создании' });
      }
    } catch (err: any) {
      setResult({ success: false, message: err.response?.data?.message || 'Произошла ошибка при создании пользователя' });
    } finally {
      setIsCreating(false);
    }
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  const selectedRole = initData?.roles.find(r => r.slug === roleSlug);
  const isDashboard = selectedRole?.targetInterface === 'dashboard';
  const isPharmacy = roleSlug && PHARMACY_ROLE_SLUGS.includes(roleSlug);
  const isStorage = roleSlug && STORAGE_ROLE_SLUGS.includes(roleSlug);
  const needsPharmacyNum = isPharmacy || isStorage;

  const isFormValid =
    username.trim().length >= 2 &&
    password.length >= 4 &&
    roleSlug !== '' &&
    (!needsPharmacyNum || pharmacyNum.trim() !== '') &&
    cityId !== '' &&
    !loginError &&
    !loginChecking;

  // ── Loading / Error states ─────────────────────────────────────────────────
  if (initLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
        <CircularProgress size={32} thickness={4} />
        <Typography sx={{ color: '#64748b', fontSize: '0.85rem' }}>Загрузка формы...</Typography>
      </Box>
    );
  }

  if (initError) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <ErrorIcon sx={{ fontSize: 40, color: '#ef4444', mb: 1 }} />
        <Typography sx={{ color: '#ef4444', fontWeight: 600, mb: 0.5 }}>{initError}</Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={loadInit} sx={{ mt: 1, textTransform: 'none' }}>
          Повторить
        </Button>
      </Box>
    );
  }

  const { roles, regions } = initData!;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: 'flex', flexDirection: 'column', gap: 0, mt: 3 }}
      noValidate
    >
      {/* Result feedback */}
      <Collapse in={result !== null}>
        <Alert
          severity={result?.success ? 'success' : 'error'}
          onClose={() => setResult(null)}
          sx={{ mb: 2, borderRadius: '8px', fontSize: '0.85rem' }}
        >
          {result?.message}
          {result?.success && result.timesheetInfo?.fileName && (
            <Typography component="span" sx={{ display: 'block', fontSize: '0.78rem', mt: 0.5, opacity: 0.85 }}>
              📄 Табельный документ: {result.timesheetInfo.fileName}
            </Typography>
          )}
        </Alert>
      </Collapse>

      {/* ── Row 1: Login + Password ── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {/* Login */}
        <TextField
          label="Логин"
          value={username}
          onChange={e => handleUsernameChange(e.target.value)}
          error={!!loginError}
          helperText={
            loginChecking ? 'Проверка...' :
              loginError || 'Имя пользователя (начинается с буквы)'
          }
          sx={{ flex: 1, minWidth: 220, ...fieldSx }}
          InputProps={{
            endAdornment: loginChecking ? (
              <InputAdornment position="end">
                <CircularProgress size={16} thickness={4} />
              </InputAdornment>
            ) : undefined
          }}
          autoComplete="off"
        />

        {/* Password */}
        <TextField
          label="Пароль"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={e => setPassword(e.target.value)}
          disabled={!!DEFAULT_PASSWORD_ROLES[roleSlug]}
          helperText={DEFAULT_PASSWORD_ROLES[roleSlug] ? 'Стандартный пароль для аптеки' : 'Минимум 4 символа'}
          sx={{ flex: 1, minWidth: 220, ...fieldSx }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ gap: 0.5 }}>
                <Tooltip title={copied ? 'Скопировано!' : 'Копировать'}>
                  <IconButton size="small" onClick={handleCopyPassword} sx={{ color: '#94a3b8' }}>
                    <CopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                {!DEFAULT_PASSWORD_ROLES[roleSlug] && (
                  <Tooltip title="Сгенерировать">
                    <IconButton size="small" onClick={handleGeneratePassword} sx={{ color: '#94a3b8' }}>
                      <RefreshIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton size="small" onClick={() => setShowPassword(v => !v)} sx={{ color: '#94a3b8' }}>
                  {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* ── Row 2: Role ── */}
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Роль
        </Typography>
        {roles.length === 0 ? (
          <Alert severity="warning" sx={{ borderRadius: '8px', fontSize: '0.83rem' }}>
            Нет доступных ролей для назначения. Обратитесь к администратору.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {roles.map(r => {
              const isSelected = roleSlug === r.slug;
              return (
                <Box
                  key={r.slug}
                  onClick={() => handleRoleChange(r.slug)}
                  sx={{
                    px: 1.5,
                    py: 0.6,
                    borderRadius: '6px',
                    border: `1.5px solid ${isSelected ? r.badge.text : r.badge.border}`,
                    backgroundColor: isSelected ? r.badge.bg : '#f8fafc',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    transition: 'all 0.12s',
                    '&:hover': {
                      borderColor: r.badge.text,
                      backgroundColor: r.badge.bg
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      backgroundColor: isSelected ? r.badge.text : r.badge.border,
                      transition: 'background-color 0.12s'
                    }}
                  />
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: isSelected ? 700 : 500, color: isSelected ? r.badge.text : '#475569', lineHeight: 1.3 }}>
                    {r.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontFamily: 'monospace', lineHeight: 1 }}>
                    {r.level}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* ── Row 3: Pharmacy / Storage field ── */}
      <Collapse in={Boolean(needsPharmacyNum)}>
        <Box sx={{ mt: 2 }}>
          <TextField
            label={isStorage ? 'Наименование склада' : 'Номер аптеки'}
            value={pharmacyNum}
            onChange={e => setPharmacyNum(e.target.value)}
            helperText={isStorage ? 'Только латиница, например: tlc' : 'Только цифры и знаки -_/, например: 301'}
            fullWidth
            sx={fieldSx}
          />
        </Box>
      </Collapse>

      {/* ── Row 4: myPharmacy (for admin-roles that track pharmacies) ── */}
      <Collapse in={Boolean(!isDashboard && roleSlug !== '')}>
        <Box sx={{ mt: 2 }}>
          <TextField
            label="Курируемые аптеки"
            value={myPharmacy}
            onChange={e => setMyPharmacy(e.target.value)}
            helperText="Номера аптек через запятую (например: 301,23,1_7). Оставьте пустым, если не требуется."
            fullWidth
            sx={fieldSx}
          />
        </Box>
      </Collapse>

      {/* ── Row 5: Region ── */}
      <Box sx={{ mt: 2 }}>
        <FormControl fullWidth sx={fieldSx}>
          <InputLabel sx={{ fontSize: '0.85rem' }}>Регион</InputLabel>
          <Select
            value={cityId}
            label="Регион"
            onChange={(e: SelectChangeEvent) => setCityId(e.target.value)}
            sx={{ borderRadius: '8px', fontSize: '0.9rem' }}
          >
            {regions.map(r => (
              <MenuItem key={r.id} value={String(r.id)} sx={{ fontSize: '0.87rem' }}>
                {r.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* ── Actions ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 3 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isFormValid || isCreating}
          disableElevation
          startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : <CreateIcon />}
          sx={{
            flex: onCancel ? 1 : undefined,
            minWidth: 160,
            fontWeight: 700,
            borderRadius: '8px',
            textTransform: 'none',
            fontSize: '0.9rem',
            py: 1
          }}
        >
          {isCreating ? 'Создание...' : 'Создать пользователя'}
        </Button>

        {onCancel && (
          <Button
            type="button"
            variant="outlined"
            onClick={onCancel}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              color: '#64748b',
              borderColor: '#e2e8f0',
              '&:hover': { borderColor: '#94a3b8', backgroundColor: '#f8fafc' }
            }}
          >
            Отмена
          </Button>
        )}
      </Box>

      {/* Progress bar while creating */}
      {isCreating && (
        <LinearProgress
          sx={{
            mt: 1.5,
            borderRadius: '4px',
            height: 3,
            backgroundColor: '#e2e8f0',
            '& .MuiLinearProgress-bar': { borderRadius: '4px' }
          }}
        />
      )}
    </Box>
  );
};

export default CreateAccount;